import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlusIcon as Plus, TrashIcon as Trash, FloppyDiskIcon as FloppyDisk, ArrowUpIcon as ArrowUp, ArrowDownIcon as ArrowDown, FileTextIcon as FileText, WrenchIcon as Wrench, LightningIcon as Lightning, GearIcon as Gear, ShieldCheckIcon as ShieldCheck, PrinterIcon as Printer, BuildingsIcon as Buildings, ReceiptIcon as Receipt } from "@phosphor-icons/react";

const clone = (o) => JSON.parse(JSON.stringify(o));
const ICONS = { Wrench, Lightning, Gear, ShieldCheck, Printer, FileText, Buildings, Receipt };
const ICON_KEYS = Object.keys(ICONS);

export function ContrattiConfigSection() {
  const [cats, setCats] = useState(null);
  const [marche, setMarche] = useState([]);
  const [modelliArr, setModelliArr] = useState([]);

  useEffect(() => {
    api.get("/contratti-config").then(({ data }) => {
      setCats(data.categorie || []);
      const ms = data.copier_marche || [];
      setMarche(ms);
      setModelliArr(ms.map((m) => (data.copier_modelli || {})[m] || []));
    }).catch(() => {});
  }, []);

  const upd = (i, k, v) => setCats((c) => { const n = clone(c); n[i][k] = v; return n; });
  const add = () => setCats((c) => [...c, { slug: "", nome: "", icona: "FileText", ordine: c.length + 1, attiva: true, descrizione: "" }]);
  const remove = (i) => setCats((c) => c.filter((_, j) => j !== i));
  const move = (i, dir) => setCats((c) => {
    const j = i + dir;
    if (j < 0 || j >= c.length) return c;
    const n = clone(c); [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const addMarca = () => { setMarche((m) => [...m, ""]); setModelliArr((a) => [...a, []]); };
  const updMarca = (i, v) => setMarche((m) => { const n = [...m]; n[i] = v; return n; });
  const removeMarca = (i) => { setMarche((m) => m.filter((_, j) => j !== i)); setModelliArr((a) => a.filter((_, j) => j !== i)); };
  const addModello = (i) => setModelliArr((a) => a.map((arr, j) => (j === i ? [...arr, ""] : arr)));
  const updModello = (i, k, v) => setModelliArr((a) => a.map((arr, j) => (j === i ? arr.map((x, l) => (l === k ? v : x)) : arr)));
  const removeModello = (i, k) => setModelliArr((a) => a.map((arr, j) => (j === i ? arr.filter((_, l) => l !== k) : arr)));
  const sortAZ = () => {
    const coll = (a, b) => (a || "").localeCompare(b || "", "it", { sensitivity: "base" });
    const pairs = marche.map((m, i) => ({ m, mods: [...(modelliArr[i] || [])].sort(coll) }));
    pairs.sort((a, b) => coll(a.m, b.m));
    setMarche(pairs.map((p) => p.m));
    setModelliArr(pairs.map((p) => p.mods));
  };

  const save = async () => {
    try {
      const copier_modelli = {};
      marche.forEach((m, i) => { const name = (m || "").trim(); if (name) copier_modelli[name] = (modelliArr[i] || []).filter((x) => (x || "").trim()); });
      const payload = {
        categorie: cats.map((c, i) => ({ ...c, ordine: i + 1 })),
        copier_marche: marche.map((m) => (m || "").trim()).filter(Boolean),
        copier_modelli,
      };
      const { data } = await api.put("/contratti-config", payload);
      setCats(data.categorie || []);
      const ms = data.copier_marche || [];
      setMarche(ms);
      setModelliArr(ms.map((m) => (data.copier_modelli || {})[m] || []));
      toast.success("Configurazione contratti salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!cats) return <Card className="rounded-sm border-border shadow-none p-6">Caricamento…</Card>;

  const IconPreview = ({ icona }) => { const I = ICONS[icona] || FileText; return <I size={18} className="text-primary" />; };

  return (
    <div className="space-y-6">
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="contratti-config-section">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><FileText size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Categorie contratto</h3>
            <p className="text-xs text-muted-foreground">Le categorie diventano i tab orizzontali nella pagina Contratti. Riordina, rinomina, attiva/disattiva.</p>
          </div>
        </div>
        <Button onClick={save} className="rounded-sm gap-2" data-testid="save-contratti-config-btn"><FloppyDisk size={16} /> Salva</Button>
      </div>

      <div className="space-y-2" data-testid="contratti-cat-list">
        {cats.map((cat, i) => (
          <div key={i} className="flex items-center gap-2 bg-secondary/50 rounded-sm p-2" data-testid={`contratti-cat-row-${i}`}>
            <div className="flex flex-col shrink-0">
              <Button variant="ghost" size="icon" className="h-5 w-6" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`contratti-cat-up-${i}`}><ArrowUp size={13} /></Button>
              <Button variant="ghost" size="icon" className="h-5 w-6" onClick={() => move(i, 1)} disabled={i === cats.length - 1} data-testid={`contratti-cat-down-${i}`}><ArrowDown size={13} /></Button>
            </div>
            <IconPreview icona={cat.icona} />
            <Select value={cat.icona || "FileText"} onValueChange={(v) => upd(i, "icona", v)}>
              <SelectTrigger className="rounded-sm h-9 w-32 bg-white shrink-0" data-testid={`contratti-cat-icon-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ICON_KEYS.map((k) => { const I = ICONS[k]; return <SelectItem key={k} value={k}><span className="flex items-center gap-2"><I size={15} /> {k}</span></SelectItem>; })}
              </SelectContent>
            </Select>
            <Input value={cat.nome || ""} onChange={(e) => upd(i, "nome", e.target.value)} placeholder="Nome categoria" className="rounded-sm h-9 bg-white w-48" data-testid={`contratti-cat-nome-${i}`} />
            <Input value={cat.descrizione || ""} onChange={(e) => upd(i, "descrizione", e.target.value)} placeholder="Descrizione" className="rounded-sm h-9 bg-white flex-1" data-testid={`contratti-cat-desc-${i}`} />
            <div className="flex items-center gap-1.5 shrink-0" title="Attiva">
              <Switch checked={cat.attiva !== false} onCheckedChange={(v) => upd(i, "attiva", v)} data-testid={`contratti-cat-active-${i}`} />
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => remove(i)} data-testid={`contratti-cat-remove-${i}`}><Trash size={14} /></Button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="rounded-sm gap-1 mt-3" onClick={add} data-testid="contratti-cat-add"><Plus size={14} /> Aggiungi categoria</Button>
    </Card>

    <Card className="rounded-sm border-border shadow-none p-6" data-testid="copier-config-section">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Printer size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Fotocopiatrici — Marche e modelli</h3>
            <p className="text-xs text-muted-foreground">Popolano i menu a tendina "Marca fotocopiatrice" e "Modello fotocopiatrice" nei contratti Noleggio e Costo Copia.</p>
          </div>
        </div>
        <Button onClick={save} className="rounded-sm gap-2" data-testid="save-copier-config-btn"><FloppyDisk size={16} /> Salva</Button>
      </div>
      <div className="mb-3">
        <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={sortAZ} data-testid="copier-sort-az"><ArrowDown size={14} /> Ordina A-Z</Button>
      </div>
      <div className="space-y-3" data-testid="copier-marche-list">
        {marche.map((marca, i) => (
          <div key={i} className="rounded-sm border border-border p-3 bg-secondary/30" data-testid={`copier-marca-row-${i}`}>
            <div className="flex items-center gap-2">
              <Printer size={15} className="text-primary shrink-0" />
              <Input value={marca} onChange={(e) => updMarca(i, e.target.value)} placeholder="Nome marca" className="rounded-sm h-9 w-56 bg-white" data-testid={`copier-marca-${i}`} />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive ml-auto" onClick={() => removeMarca(i)} data-testid={`copier-marca-remove-${i}`}><Trash size={14} /></Button>
            </div>
            <div className="mt-2 pl-6 space-y-1.5">
              {(modelliArr[i] || []).map((mod, j) => (
                <div key={j} className="flex items-center gap-2">
                  <Input value={mod} onChange={(e) => updModello(i, j, e.target.value)} placeholder="Modello" className="rounded-sm h-8 w-64 bg-white" data-testid={`copier-modello-${i}-${j}`} />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeModello(i, j)} data-testid={`copier-modello-remove-${i}-${j}`}><Trash size={13} /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="rounded-sm gap-1 h-7" onClick={() => addModello(i)} data-testid={`copier-modello-add-${i}`}><Plus size={13} /> Aggiungi modello</Button>
            </div>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" className="rounded-sm gap-1 mt-3" onClick={addMarca} data-testid="copier-marca-add"><Plus size={14} /> Aggiungi marca</Button>
    </Card>
    </div>
  );
}
